import { Injectable } from '@angular/core';
import { WorkItemActionType } from 'src/app/models/client-microservice/client-configuration';
import {
  DatePattern,
  DatePatternDaysOfWeek
} from 'src/app/models/date-pattern';
import {
  DeviationType,
  ManipulateGeneratedItems,
  PatternType,
  RepeatTypes
} from 'src/app/models/enums';
import { MonthlySettings } from 'src/app/models/monthly-settings';
import {
  DatePatternBaseDto,
  DatePatternType,
  DayType,
  HolidayTaskRule
} from 'src/app/models/process/date-pattern-base-dto';
import { DatePatternMonthlyDto } from 'src/app/models/process/date-pattern-monthly-dto';
import { DatePatternWeeklyDto } from 'src/app/models/process/date-pattern-weekly-dto';
import {
  DatePatternYearlyDto,
  Months
} from 'src/app/models/process/date-pattern-yearly-dto';
import { YearlySettings } from 'src/app/models/yearly-settings';

@Injectable({
  providedIn: 'root'
})
export class DtoToMonoDatePatternService {
  dto2mono(dto: DatePatternBaseDto): DatePattern {
    const mono = new DatePattern();

    mono.isNew = dto.isNew;
    mono.id = dto.externalId;
    mono.patternType = PatternType.Pattern;
    mono.name = dto.name;
    mono.summary = dto.description;
    mono.repeat = dto.interval;
    mono.periodRule = dto.period;
    mono.startTask = dto.numberOfDaysToStartBeforeDeadline;
    mono.startType =
      dto.daysToStartBeforeDeadlineType == DayType.WorkingDays
        ? DeviationType.WorkingDays
        : DeviationType.Days;
    mono.countryCodes = dto.holidayRuleCountryCodes;
    mono.order = dto.order;
    mono.intervalFromDate = this.convertToMiddayUTC(dto.intervalFromDate);
    // if (!!dto.intervalFromDate) {
    //   let date = new Date(dto.intervalFromDate);
    //   date.setUTCHours(12);
    //   mono.intervalFromDate = date;
    // }
    mono.holidayTaskRule = this.convertDto2MonoHolidayTaskRule(
      dto.holidayRuleType
    );
    mono.customHolidays = dto.customHolidays;
    mono.useCountryCodes =
      dto.customHolidays == null || dto.customHolidays.length == 0;
    mono.repeatType = this.convertDto2MonoType(dto.type);
    mono.saveOption = this.convertDto2MonoSaveOption(dto.saveOption);

    if (dto.type == DatePatternType.Daily) {
      // do nothing
    } else if (dto.type == DatePatternType.Info) {
      // do nothing
    } else if (dto.type == DatePatternType.Monthly) {
      const dtoMonthly = dto as DatePatternMonthlyDto;
      const monthlyPattern = new MonthlySettings();
      monthlyPattern.monthlyDays = dtoMonthly.days;
      monthlyPattern.monthlyWorkingDays = dtoMonthly.workingDays;
      monthlyPattern.deviation = dtoMonthly.deviation;
      monthlyPattern.deviationType =
        dtoMonthly.deviationType == DayType.WorkingDays
          ? DeviationType.WorkingDays
          : DeviationType.Days;
      mono.monthlyPattern = monthlyPattern;
    } else if (dto.type == DatePatternType.Weekly) {
      const dtoWeekly = dto as DatePatternWeeklyDto;
      const daysOfWeek: number[] = [];
      if (dtoWeekly.monday) daysOfWeek.push(DatePatternDaysOfWeek.Monday);
      if (dtoWeekly.tuesday) daysOfWeek.push(DatePatternDaysOfWeek.Tuesday);
      if (dtoWeekly.wednesday) daysOfWeek.push(DatePatternDaysOfWeek.Wednesday);
      if (dtoWeekly.thursday) daysOfWeek.push(DatePatternDaysOfWeek.Thursday);
      if (dtoWeekly.friday) daysOfWeek.push(DatePatternDaysOfWeek.Friday);
      if (dtoWeekly.saturday) daysOfWeek.push(DatePatternDaysOfWeek.Saturday);
      if (dtoWeekly.sunday) daysOfWeek.push(DatePatternDaysOfWeek.Sunday);
      mono.daysOfWeek = daysOfWeek;
    } else if (dto.type == DatePatternType.Yearly) {
      const dtoYearly = dto as DatePatternYearlyDto;
      mono.isStartTaskCustom = dtoYearly.useCustomStartTask;
      mono.yearlyPatterns = dtoYearly.months?.map(yearlyPattern => {
        const monoYearlyPattern = new YearlySettings();
        monoYearlyPattern.workingDays = yearlyPattern.workingDays;
        monoYearlyPattern.days = yearlyPattern.days;
        monoYearlyPattern.startType =
          yearlyPattern.customDaysToStartBeforeDeadlineType ==
          DayType.WorkingDays
            ? DeviationType.WorkingDays
            : DeviationType.Days;
        monoYearlyPattern.startTask =
          yearlyPattern.customNumberOfDaysToStartBeforeDeadline;
        monoYearlyPattern.month = this.convertDto2MonoMonth(
          yearlyPattern.month
        );
        monoYearlyPattern.deviation = yearlyPattern.deviation;
        monoYearlyPattern.deviationType =
          yearlyPattern.deviationType == DayType.WorkingDays
            ? DeviationType.WorkingDays
            : DeviationType.Days;
        return monoYearlyPattern;
      });
      dto = dtoYearly;
    }

    return mono;
  }

  private convertToMiddayUTC(input: string): Date {
    if (!input) {
      return null;
    }

    // Ensure the input is treated as UTC by appending "Z" if no timezone exists
    const formattedInput =
      input.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(input)
        ? input
        : `${input}Z`;

    const date = new Date(formattedInput);

    if (isNaN(date.getTime())) {
      return null;
    }

    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = date.getUTCDate();

    return new Date(Date.UTC(year, month, day, 12, 0, 0)); // Midday UTC
  }

  private convertDto2MonoHolidayTaskRule(
    holidayTaskRule: HolidayTaskRule
  ): number {
    if (holidayTaskRule == HolidayTaskRule.Previous) {
      return 1;
    } else if (holidayTaskRule == HolidayTaskRule.Next) {
      return 2;
    } else {
      return 0;
    }
  }

  private convertDto2MonoMonth(month: Months): number {
    switch (month) {
      case Months.January:
        return 1;
      case Months.February:
        return 2;
      case Months.March:
        return 3;
      case Months.April:
        return 4;
      case Months.May:
        return 5;
      case Months.June:
        return 6;
      case Months.July:
        return 7;
      case Months.August:
        return 8;
      case Months.September:
        return 9;
      case Months.October:
        return 10;
      case Months.November:
        return 11;
      case Months.December:
        return 12;
    }
  }

  private convertDto2MonoSaveOption(
    saveOption: WorkItemActionType
  ): ManipulateGeneratedItems {
    if (saveOption == WorkItemActionType.Remove) {
      return ManipulateGeneratedItems.Remove;
    } else if (saveOption == WorkItemActionType.Update) {
      return ManipulateGeneratedItems.Update;
    } else {
      return ManipulateGeneratedItems.Keep;
    }
  }

  private convertDto2MonoType(type: DatePatternType): RepeatTypes {
    switch (type) {
      case DatePatternType.Info:
        return RepeatTypes.Info;
      case DatePatternType.Daily:
        return RepeatTypes.Daily;
      case DatePatternType.Weekly:
        return RepeatTypes.Weekly;
      case DatePatternType.Monthly:
        return RepeatTypes.Monthly;
      case DatePatternType.Yearly:
        return RepeatTypes.Yearly;
      default:
        return RepeatTypes.Daily;
    }
  }
}
